import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type IncidentStatus = components["schemas"]["IncidentStatus"];
export type IncidentResponse = components["schemas"]["IncidentResponse"];
export type IncidentListItem = components["schemas"]["IncidentListItem"];
export type IncidentListResponse = components["schemas"]["IncidentListResponse"];
export type CreateIncidentRequest = components["schemas"]["CreateIncidentRequest"];
export type UpdateIncidentRequest = components["schemas"]["UpdateIncidentRequest"];
export type SetOwnerRequest = components["schemas"]["SetOwnerRequest"];
export type SetAssigneesRequest = components["schemas"]["SetAssigneesRequest"];
export type SetTagsRequest = components["schemas"]["SetTagsRequest"];
export type StatusChangeRequest = components["schemas"]["StatusChangeRequest"];
export type ReopenIncidentRequest =
  components["schemas"]["ReopenIncidentRequest"];
export type SetLegalHoldRequest =
  components["schemas"]["SetLegalHoldRequest"];
export type TimelineListResponse = components["schemas"]["TimelineListResponse"];
export type TimelineEventResponse = components["schemas"]["TimelineEventResponse"];
export type CreateTimelineEventRequest =
  components["schemas"]["CreateTimelineEventRequest"];
export type ManualTimelineType = components["schemas"]["ManualTimelineType"];
export type IncidentSortField = components["schemas"]["IncidentSortField"];
export type SortDirection = "asc" | "desc";
export type IncidentArchiveFilter = components["schemas"]["IncidentArchiveFilter"];

export interface IncidentFilters {
  /** Free-text search over number, title and description (F-011, CD-015). */
  q?: string;
  /** Sort column and direction (F-011); the server defaults to createdAt/desc. */
  sort?: IncidentSortField;
  dir?: SortDirection;
  status?: IncidentStatus;
  /** Comma-joined multi-status filter (dashboard drilldowns, F-020). */
  statuses?: string;
  severityId?: string;
  ownerId?: string;
  categoryId?: string;
  /** F-011 Bereich / Tag filters. */
  areaId?: string;
  tagId?: string;
  /** ISO timestamp lower bound on createdAt. */
  createdAfter?: string;
  /** F-011 Zeitraum on the Entdeckungszeitpunkt: from (inclusive) .. before
   *  (exclusive), both ISO timestamps. */
  discoveredFrom?: string;
  discoveredBefore?: string;
  /** Resolved incidents that still lack a postmortem. */
  missingPostmortem?: boolean;
  /** Incidents with overdue follow-up tasks. */
  overdueFollowup?: boolean;
  /** Only incidents the current user owns or is assigned to ("Meine"). */
  mine?: boolean;
  /** GR-004 archive; the server leaves archived incidents out when unset. */
  archived?: IncidentArchiveFilter;
  limit?: number;
}

export const incidentKeys = {
  all: ["incidents"] as const,
  list: (filters: IncidentFilters) => ["incidents", "list", filters] as const,
  detail: (id: string) => ["incidents", "detail", id] as const,
  timeline: (id: string) => ["incidents", "timeline", id] as const,
};

/** Build a query string, omitting empty/undefined values (keeps keys clean). */
function buildQuery(
  params: Record<string, string | number | undefined>,
): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") sp.set(key, String(value));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Pure filter -> query-param map, shared by the list query and the CSV export
 * (F-012: the export takes EXACTLY the list filters). Deliberately excludes
 * the pagination knobs (cursor/limit) - the export always covers the full
 * filtered set; the list query adds them on top.
 */
export function incidentFilterParams(
  filters: IncidentFilters,
): Record<string, string | undefined> {
  return {
    q: filters.q,
    sort: filters.sort,
    dir: filters.dir,
    status: filters.status,
    statuses: filters.statuses,
    severityId: filters.severityId,
    ownerId: filters.ownerId,
    categoryId: filters.categoryId,
    areaId: filters.areaId,
    tagId: filters.tagId,
    createdAfter: filters.createdAfter,
    discoveredFrom: filters.discoveredFrom,
    discoveredBefore: filters.discoveredBefore,
    // The contract types the flags as "true" | "false"; omit when unset.
    missingPostmortem: filters.missingPostmortem ? "true" : undefined,
    overdueFollowup: filters.overdueFollowup ? "true" : undefined,
    mine: filters.mine ? "true" : undefined,
    archived: filters.archived,
  };
}

/** Serialized filter query string ("" when no filter is active). */
export function incidentFilterQuery(filters: IncidentFilters): string {
  return buildQuery(incidentFilterParams(filters));
}

/**
 * Strong ETag for the If-Match precondition (ADR-FE-006). The incident body
 * carries a numeric `version`; the header value is that number wrapped in
 * double quotes, e.g. version 3 -> `"3"`.
 */
export function ifMatch(version: number): string {
  return `"${version}"`;
}

/** GET /incidents - cursor-paginated list; "Mehr laden" walks nextCursor. */
export function useIncidents(filters: IncidentFilters = {}) {
  return useInfiniteQuery({
    queryKey: incidentKeys.list(filters),
    queryFn: ({ pageParam }) =>
      apiFetch<IncidentListResponse>(
        `/incidents${buildQuery({
          ...incidentFilterParams(filters),
          limit: filters.limit,
          cursor: pageParam,
        })}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: IncidentListResponse) =>
      last.nextCursor ?? undefined,
  });
}

/** GET /incidents/{id}. */
export function useIncident(id: string) {
  return useQuery({
    queryKey: incidentKeys.detail(id),
    queryFn: () => apiFetch<IncidentResponse>(`/incidents/${id}`),
    enabled: id.length > 0,
  });
}

/** The largest page the api serves (CursorQuerySchema caps `limit` at 100). */
const TIMELINE_PAGE_SIZE = 100;

/**
 * Walk a cursor-paginated list to its end. Stops on a repeated cursor, so a
 * misbehaving server cannot hold the page in a loop.
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<{ items: T[]; nextCursor: string | null }>,
): Promise<T[]> {
  const items: T[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    if (!page.nextCursor || seen.has(page.nextCursor)) return items;
    seen.add(page.nextCursor);
    cursor = page.nextCursor;
  }
}

/**
 * GET /incidents/{id}/timeline - the WHOLE timeline (created_at ASC). [F-005]
 * asks for the chronological timeline, not its first page: the view used to
 * stop at the api's default page of 50 entries, so a long incident lost its
 * latest ones without a word.
 */
export function useTimeline(id: string) {
  return useQuery({
    queryKey: incidentKeys.timeline(id),
    queryFn: async (): Promise<TimelineListResponse> => ({
      items: await fetchAllPages((cursor) =>
        apiFetch<TimelineListResponse>(
          `/incidents/${id}/timeline${buildQuery({ limit: TIMELINE_PAGE_SIZE, cursor })}`,
        ),
      ),
      nextCursor: null,
    }),
    enabled: id.length > 0,
  });
}

/** POST /incidents. Navigation to the new detail happens in the caller. */
export function useCreateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIncidentRequest) =>
      apiFetch<IncidentResponse>("/incidents", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export interface ChangeStatusVars {
  toStatus: IncidentStatus;
  /** Current loaded version, sent as the If-Match precondition. */
  version: number;
}

/** PATCH /incidents/{id}/status (GR-001 graph, If-Match precondition). */
export function useChangeStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ toStatus, version }: ChangeStatusVars) =>
      apiFetch<IncidentResponse>(`/incidents/${id}/status`, {
        method: "PATCH",
        headers: { "If-Match": ifMatch(version) },
        body: JSON.stringify({ toStatus } satisfies StatusChangeRequest),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(incidentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.timeline(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export interface ReopenIncidentVars {
  body: ReopenIncidentRequest;
  /** Current loaded version, sent as the If-Match precondition. */
  version: number;
}

/** POST /incidents/{id}/reopen (F-018/GR-007, If-Match precondition). */
export function useReopenIncident(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, version }: ReopenIncidentVars) =>
      apiFetch<IncidentResponse>(`/incidents/${id}/reopen`, {
        method: "POST",
        headers: { "If-Match": ifMatch(version) },
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(incidentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.timeline(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export interface SetLegalHoldVars {
  body: SetLegalHoldRequest;
  /** Current loaded version, sent as the If-Match precondition. */
  version: number;
}

/** PUT /incidents/{id}/legal-hold (GR-004, If-Match precondition, Admin). */
export function useSetLegalHold(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, version }: SetLegalHoldVars) =>
      apiFetch<IncidentResponse>(`/incidents/${id}/legal-hold`, {
        method: "PUT",
        headers: { "If-Match": ifMatch(version) },
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(incidentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.timeline(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export interface ClearLegalHoldVars {
  /** Current loaded version, sent as the If-Match precondition. */
  version: number;
}

/** DELETE /incidents/{id}/legal-hold (GR-004, If-Match precondition, Admin). */
export function useClearLegalHold(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ version }: ClearLegalHoldVars) =>
      apiFetch<IncidentResponse>(`/incidents/${id}/legal-hold`, {
        method: "DELETE",
        headers: { "If-Match": ifMatch(version) },
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(incidentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.timeline(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export interface UpdateIncidentVars {
  body: UpdateIncidentRequest;
  /** Current loaded version, sent as the If-Match precondition. */
  version: number;
}

/**
 * After any write to one incident: the detail takes the response, the lists
 * refetch. The timeline lives under the same prefix and refetches with them -
 * Owner, Bearbeiter and tag changes write entries of their own ([F-005]).
 */
function useIncidentWrite<V extends { version: number }>(
  id: string,
  call: (vars: V) => Promise<IncidentResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: (updated) => {
      queryClient.setQueryData(incidentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

/** PATCH /incidents/{id}: title, description, category, severity, areas (`incident.edit`). */
export function useUpdateIncident(id: string) {
  return useIncidentWrite(id, ({ body, version }: UpdateIncidentVars) =>
    apiFetch<IncidentResponse>(`/incidents/${id}`, {
      method: "PATCH",
      headers: { "If-Match": ifMatch(version) },
      body: JSON.stringify(body),
    }),
  );
}

/** PUT /incidents/{id}/owner: name or clear the Owner (`incident.assign` / `incident.reassign-inactive-owner`). */
export function useSetOwner(id: string) {
  return useIncidentWrite(id, ({ ownerId, version }: SetOwnerRequest & { version: number }) =>
    apiFetch<IncidentResponse>(`/incidents/${id}/owner`, {
      method: "PUT",
      headers: { "If-Match": ifMatch(version) },
      body: JSON.stringify({ ownerId }),
    }),
  );
}

/** POST /incidents/{id}/take: take an incident without an Owner (`incident.take`). */
export function useTakeIncident(id: string) {
  return useIncidentWrite(id, ({ version }: { version: number }) =>
    apiFetch<IncidentResponse>(`/incidents/${id}/take`, {
      method: "POST",
      headers: { "If-Match": ifMatch(version) },
    }),
  );
}

/** PUT /incidents/{id}/assignees: the assigned Bearbeiter (`incident.assign`). */
export function useSetAssignees(id: string) {
  return useIncidentWrite(id, ({ assigneeIds, version }: SetAssigneesRequest & { version: number }) =>
    apiFetch<IncidentResponse>(`/incidents/${id}/assignees`, {
      method: "PUT",
      headers: { "If-Match": ifMatch(version) },
      body: JSON.stringify({ assigneeIds }),
    }),
  );
}

/** PUT /incidents/{id}/tags: the incident's tags (`incident.tags`). */
export function useSetTags(id: string) {
  return useIncidentWrite(id, ({ tagIds, version }: SetTagsRequest & { version: number }) =>
    apiFetch<IncidentResponse>(`/incidents/${id}/tags`, {
      method: "PUT",
      headers: { "If-Match": ifMatch(version) },
      body: JSON.stringify({ tagIds }),
    }),
  );
}

/**
 * The list's inline edits (CD-006 / LP-027): the same endpoints, precondition
 * and audit as the detail view - only the id travels with the call because one
 * hook serves every row. Severity and category are `incident.edit`; the Owner
 * is the owner endpoint.
 */
export function useUpdateIncidentInList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      field,
      value,
    }: {
      id: string;
      version: number;
      field: "severityId" | "categoryId" | "ownerId";
      value: string | null;
    }) =>
      field === "ownerId"
        ? apiFetch<IncidentResponse>(`/incidents/${id}/owner`, {
            method: "PUT",
            headers: { "If-Match": ifMatch(version) },
            body: JSON.stringify({ ownerId: value }),
          })
        : apiFetch<IncidentResponse>(`/incidents/${id}`, {
            method: "PATCH",
            headers: { "If-Match": ifMatch(version) },
            body: JSON.stringify({ [field]: value }),
          }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

/** POST /incidents/{id}/timeline (GR-003 restricts Melder-only to kommentar). */
export function useAddTimelineEvent(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTimelineEventRequest) =>
      apiFetch<TimelineEventResponse>(`/incidents/${id}/timeline`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.timeline(id) });
    },
  });
}
